import { Injectable, inject } from '@angular/core';
import {
  collection,
  CollectionReference,
  query,
  orderBy,
  doc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { FIREBASE_AUTH, FIRESTORE } from '@core/firebase/firebase.tokens';
import { collectionData$ } from '@core/firebase/firebase-rxjs.utils';
import { SaypStatementPdf } from '@save-as-you-pay/models/sayp-statement-pdf.model';

@Injectable({
  providedIn: 'root',
})
export class SaypStatementPdfsService {
  private readonly firestore = inject(FIRESTORE);
  private readonly auth = inject(FIREBASE_AUTH);
  private readonly collectionRef: CollectionReference;

  constructor() {
    this.collectionRef = collection(this.firestore, 'saypStatementPdfs');
  }

  streamAllPdfs(): Observable<SaypStatementPdf[]> {
    const q = query(this.collectionRef, orderBy('createdAt', 'desc'));
    return collectionData$(q, { idField: 'id' }).pipe(
      map(data => data as unknown as SaypStatementPdf[])
    );
  }

  async markPdfDownloaded(pdfId: string): Promise<void> {
    const docRef = doc(this.firestore, 'saypStatementPdfs', pdfId);
    const currentUser = this.auth.currentUser;

    if (!currentUser) {
      throw new Error('User must be authenticated to mark PDF as downloaded');
    }

    await updateDoc(docRef, {
      didDownloadStatement: true,
      lastUpdated: Timestamp.now(),
      lastUpdatedBy: currentUser.uid,
    });
  }

  async requestEmailSend(pdfIds: string[]): Promise<void> {
    const currentUser = this.auth.currentUser;

    if (!currentUser) {
      throw new Error('User must be authenticated to request email send');
    }

    const updatePromises = pdfIds.map(pdfId => {
      const docRef = doc(this.firestore, 'saypStatementPdfs', pdfId);
      return updateDoc(docRef, {
        emailSendRequested: true,
        lastUpdated: Timestamp.now(),
        lastUpdatedBy: currentUser.uid,
      });
    });

    await Promise.all(updatePromises);
  }
}
